<template>
  <div
    v-if="props.usps.length"
    class="usps"
    :class="placementClass"
  >
    <slot />

    <h3
      v-if="props.title"
      class="usps__title"
    >
      {{ props.title }}
    </h3>

    <ul
      class="usps__list"
      :class="{
        'usps--horizontal': props.horizontal,
        'usps--vertical': !props.horizontal,
      }"
    >
      <li
        v-for="(usp, index) in props.usps"
        :key="usp.id"
        class="usps__item"
        :class="{ 'usps__item--active': index === activeIndex }"
      >
        <IconCheck class="usps__icon usps__icon--check" />

        <component
          :is="getElement(usp.Link)"
          v-bind="usp.Link ? { to: usp.Link } : {}"
          class="usps__label"
        >
          <template
            v-for="(part, partIndex) in getLabelParts(usp)"
            :key="`${usp.id}-${partIndex}`"
          >
            <span
              v-if="part.highlighted"
              class="usps__highlight"
              :class="getHighlightClasses(usp.HighlightStyle)"
              :style="getHighlightStyle(usp.HighlightColor)"
            >
              {{ part.text }}
            </span>

            <template v-else>
              {{ part.text }}
            </template>
          </template>
        </component>
      </li>
    </ul>
  </div>
</template>

<script setup lang="ts">
import layoutQuery from '~layers/app/graphql/layout.gql'
import { AppLink } from '#components'

type UspHighlightStyle
  = | 'none'
    | 'bold'
    | 'underline'
    | 'bold_underline'

type Usp = {
  id: string | number
  Label?: string | null
  Description?: string | null
  Link?: string | null
  HighlightText?: string | null
  HighlightColor?: string | null
  HighlightStyle?: UspHighlightStyle | null
}

type UspTextPart = {
  text: string
  highlighted: boolean
}

type DeliveryPromise = {
  name: string
  key: string
  content: string
}

const props = withDefaults(defineProps<{
  title?: string
  usps?: Usp[]
  horizontal?: boolean
  placement?: string
}>(), {
  title: '',
  usps: () => [],
  horizontal: false,
  placement: '',
})

const { public: config } = useRuntimeConfig()

const { data } = await useAsyncQuery(
  layoutQuery,
  {
    locale: config.strapiLocale,
  },
)

const activeIndex = ref(0)

let intervalId: ReturnType<typeof setInterval> | undefined

const placementClass = computed(() => {
  return props.placement
    ? `usps--${props.placement}`
    : undefined
})

const deliveryPromise = computed<DeliveryPromise | null>(() => {
  const day = data.value?.dayOfWeek

  const deliveryPromises = data.value?.deliveryPromise as
    | Record<string, string | undefined>
    | undefined

  if (!day || !deliveryPromises) {
    return null
  }

  const dpKey = Object.keys(deliveryPromises)
    .find(key => key.includes(`_${day}`))

  if (!dpKey) {
    return null
  }

  const content = deliveryPromises[dpKey]

  if (!content) {
    return null
  }

  const [name, ...keyParts] = dpKey.split('_')

  return {
    name,
    key: keyParts.join('_'),
    content,
  }
})

const deliveryPromiseContent = computed(() => {
  return deliveryPromise.value?.content ?? ''
})

const label = (usp: Usp): string => {
  const text = usp.Description?.length
    ? usp.Description
    : (usp.Label ?? '')

  return text.replace(
    '{dynamic-delivery-promise}',
    deliveryPromiseContent.value,
  )
}

const escapeRegExp = (value: string): string => {
  return value.replace(/[.*+?^${}()|[\]\\]/g, '\\$&')
}

const getLabelParts = (usp: Usp): UspTextPart[] => {
  const text = label(usp)
  const highlightText = usp.HighlightText?.trim()

  if (!highlightText || !text) {
    return [{
      text,
      highlighted: false,
    }]
  }

  const expression = new RegExp(
    `(${escapeRegExp(highlightText)})`,
    'gi',
  )

  const normalizedHighlightText = highlightText.toLowerCase()

  return text
    .split(expression)
    .filter(part => part.length > 0)
    .map(part => ({
      text: part,
      highlighted:
            part.toLowerCase() === normalizedHighlightText,
    }))
}

const getHighlightClasses = (
  style?: UspHighlightStyle | null,
): Record<string, boolean> => {
  return {
    'usps__highlight--bold':
        style === 'bold'
        || style === 'bold_underline',

    'usps__highlight--underline':
        style === 'underline'
        || style === 'bold_underline',
  }
}

const isValidHexColor = (
  color?: string | null,
): color is string => {
  if (!color) {
    return false
  }

  return /^#(?:[0-9a-f]{3}|[0-9a-f]{6})$/i.test(
    color.trim(),
  )
}

const getHighlightStyle = (
  color?: string | null,
): Record<string, string> | undefined => {
  if (!isValidHexColor(color)) {
    return undefined
  }

  return {
    color: color.trim(),
  }
}

const getElement = (url: Usp['Link']) => {
  return url ? AppLink : 'span'
}

onMounted(() => {
  if (props.usps.length <= 1) {
    return
  }

  intervalId = setInterval(() => {
    activeIndex.value
      = (activeIndex.value + 1) % props.usps.length
  }, 5000)
})

onBeforeUnmount(() => {
  if (intervalId) {
    clearInterval(intervalId)
  }
})
</script>

<style>
@import url("~layers/app/components/App/Usps.css");
</style>
